// React imports
import { useState } from 'react';

// Generic imports
import _ from 'lodash';

// Material UI imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { green } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleCheck, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import SendEmailUsingExternalServiceDialog from './SendEmailUsingExternalServiceDialog.js';
import email from '../utils/email';

function SendNewEmailDialog(props) {
    const { onSent, onClose, emailLoginInfo, org, bodyFooter, accesstoken, seperated, groupBy, locked, onBack } = props;
    const [ isSending, setIsSending ] = useState(false);
    const [ isDrafting, setIsDrafting ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ sent, setSent ] = useState(false);

    async function apiSend({ apiFunc, body, subject, toRecipients, ccRecipients }) {
        setError(null);
        try {
            const _body = body + (bodyFooter ? `<br/><br/><div style="font-size: 9px; color: #888;">${bodyFooter}</div>` : '')
            let results = null;
            let recipientGroups;
            if (groupBy) {
                const grouped = _.groupBy(toRecipients, '_group');
                // Recipients without a _group (new/manually-added addresses) get individual emails
                const ungrouped = grouped['undefined'] || [];
                delete grouped['undefined'];
                recipientGroups = Object.values(grouped);
                // Send individual emails to ungrouped recipients so they don't see each other
                for (const addr of ungrouped) {
                    recipientGroups.push([addr]);
                }
            } else if (seperated) {
                recipientGroups = toRecipients.map(a => [a]);
            } else {
                recipientGroups = [toRecipients];
            }
            if (apiFunc === 'sendMail') {
                results = await Promise.all(recipientGroups.map(_toRecipients => email.sendEmail({
                    org,
                    tokens: emailLoginInfo.tokens,
                    toRecipients: _toRecipients,
                    ccRecipients,
                    subject,
                    body: _body,
                    monadaAccesstoken: accesstoken,
                    monadaOrganizationId: org.id,
                    useQueue: true,
                })));
            } else {
                results = await Promise.all(recipientGroups.map(_toRecipients => email.createDraft({
                    org,
                    tokens: emailLoginInfo.tokens,
                    toRecipients: _toRecipients,
                    ccRecipients,
                    subject,
                    body: _body,
                    monadaAccesstoken: accesstoken,
                    monadaOrganizationId: org.id
                })));
            }
            onSent(results.map((r, i) => {
                if (r.status === 'pending') {
                    return {
                        status: 'pending',
                        pendingId: r.id,
                        createdAt: r.createdAt,
                        toRecipients: recipientGroups[i],
                        ccRecipients: ccRecipients,
                        orgToRecipients: props.toRecipients
                    }
                }
                return {
                    conversationId: r.conversationId,
                    internetMessageId: r.internetMessageId,
                    toRecipients: recipientGroups[i],
                    ccRecipients: ccRecipients,
                    orgToRecipients: props.toRecipients
                }
            }));
            setSent(true);
        } catch (e) {
            console.error(e);
            setError(e.message);
            setIsSending(false);
            setIsDrafting(false);
        }
    }

    const onSend = async ({ body, subject, toRecipients, ccRecipients }) => {
        setIsSending(true);
        await apiSend({ apiFunc: 'sendMail', body, subject, toRecipients, ccRecipients });
    };

    const onDraft = async ({ body, subject, toRecipients, ccRecipients }) => {
        setIsDrafting(true);
        await apiSend({ apiFunc: 'messages', body, subject, toRecipients, ccRecipients });
    };

    if (sent) return (
        <Box sx={{ maxWidth: '75vw', minWidth: '75vw', p: 3 }} >
            <Box sx={{ padding: '24px 0px', color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faCircleCheck} beat size='4x' style={{ animationIterationCount: 1 }} />
            </Box>
            <Box sx={{ fontWeight: 800, color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isDrafting ? 'Draft created successfully!' : 'Email sent successfully!'}
            </Box>
            <Box sx={{ display: 'flex', marginTop: '24px', flexDirection: 'row-reverse' }}>
                <Button
                    color='success'
                    variant='outlined'
                    onClick={() => onClose()}
                >
                    Close
                </Button>
            </Box>
        </Box>
    )

    return (
        <SendEmailUsingExternalServiceDialog
            {...props}
            receipientEditable={locked ? 'disabled' : 'multiple' }
            buttonsJustify={'flex-end'}
            buttons={_.compact([onBack ? {
                variant: 'outlined',
                color: 'secondary',
                func: onBack,
                sx: { width: '200px', minWidth: '200px' },
                children: <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                    &nbsp;Back
                </Box>
            } : null, {
                variant: 'contained',
                color: 'primary',
                func: onSend,
                sx: { width: '200px', minWidth: '200px' },
                disabled: isSending || isDrafting,
                children: isSending ? <CircularProgress size={16} /> :
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        &nbsp;
                        Send
                    </Box>

            }])}
            footer={error ? <Alert severity='error'>{error}</Alert> : null}
            noBorderBottom={seperated || groupBy}
            editorFooter={<>
                {groupBy ? (
                    <Box sx={{ padding: '12px', border: `1px solid #ddd`, borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
                        <Alert severity='info'>
                            <Typography variant='caption'>
                                One email will be sent per supplier. All contacts of the same supplier will be in the TO field. Manually added addresses will receive individual emails.
                            </Typography>
                        </Alert>
                    </Box>
                ) : seperated ? (
                    <Box sx={{ padding: '12px', border: `1px solid #ddd`, borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
                        <Alert severity='info'>
                            <Typography variant='caption'>
                                Recipients will get individual emails and won't see that it was sent to others.
                            </Typography>
                        </Alert>
                    </Box>
                ) : null}
            </>}
        />
    )
}

export default SendNewEmailDialog;
